import { useEffect, useMemo } from "react";
import { OrderDetailsViewModel } from "../../viewmodels/orders/OrderDetailsViewModel";

import { useParams } from "react-router-dom";

import { connect } from "react-redux";

import * as actions from "../../store/actions/index";

import { withRouter } from "../../shared/utility";

import EmptyModal from "../../components/modals/EmptyModal";
import SlideOver from "../../components/slideOver/SlideOver";
import CreateNewOrderForm from "../../components/forms/orders/CreateNewOrderForm";
import OperationalDataTableSkeleton from "../../components/operational/OperationalDataTableSkeleton";
import FormSectionLoadingSkeleton from "../../components/operational/FormSectionLoadingSkeleton";
import AuditActionLink from "../../components/audit/AuditActionLink";
import { buildOrderSummary } from "./orderDetailsHelpers";
import {
  USER_DEPARTMENT_CODE,
  USER_TYPE_CODE,
} from "../../shared/userReferenceCodes";

function OrderDetails(props) {
  const { addSnackbar, logout, user_type } = props;

  const { orderId } = useParams();

  const {
    customers,
    getCustomersForDropdown,
    getMyProducts,
    resolveProductPrice,
    loading,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    showErrorModal,
    updateOrderApiCall,
    order,
    getOrderDetails,
    onUpdateOrderItem,
    onUpdateOrderItemUnitPrice,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
    deleteOrderItemApiCall,
    addProductToOrderApiCall,
    employeeOptions,
    getUsersForDropdown,
    printOrder,
    createShipmentFromOrder,
    saving,
  } = OrderDetailsViewModel(orderId, logout, addSnackbar);

  const orderSummary = useMemo(() => buildOrderSummary(order), [order]);
  const shipments = order?.shipments ?? [];
  const allowCreateShipment = order?.can_create_shipment === true;
  const createdByLabel = order?.created_by?.label ?? order?.created_by ?? "—";

  const apiFunctionCalls = async function () {
    getUsersForDropdown(
      [USER_TYPE_CODE.REGULAR, USER_TYPE_CODE.SUPERVISOR],
      [USER_DEPARTMENT_CODE.ALL, USER_DEPARTMENT_CODE.ORDERS],
    );
    const callResult = await getCustomersForDropdown();
    if (callResult?.success) {
      getOrderDetails();
    }
  };

  useEffect(() => {
    apiFunctionCalls();
  }, []);

  const handleSave = () => {
    document.getElementById("order-details-save")?.click();
  };

  return (
    <div className="qatrace-screen qatrace-module-page">
      <div className="mt-2 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              {order ? `Order ${order.po_number}` : "Order details"}
            </h1>
            {order ? (
              <p className="mt-1 text-sm text-slate-500">
                Created by {createdByLabel} on{" "}
                {order.created_at
                  ? new Date(order.created_at).toLocaleString(undefined, {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "numeric",
                      minute: "2-digit",
                    })
                  : "—"}
              </p>
            ) : null}
          </div>
          {order ? (
            <div className="flex flex-wrap items-center gap-2">
              <AuditActionLink
                slug="order"
                id={order.id}
                name={order.po_number}
              />
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          ) : null}
        </div>

        <div className="mt-5">
          {order ? (
            <>
              <CreateNewOrderForm
                customers={customers}
                getMyProducts={getMyProducts}
                resolveProductPrice={resolveProductPrice}
                isEdit={true}
                loading={loading}
                order={order}
                setEmptyModelOpen={setEmptyModelOpen}
                showErrorModal={showErrorModal}
                updateOrderApiCall={updateOrderApiCall}
                onUpdateOrderItem={onUpdateOrderItem}
                onUpdateOrderItemUnitPrice={onUpdateOrderItemUnitPrice}
                onCreatePriceSplit={onCreatePriceSplit}
                onUpdatePriceSplit={onUpdatePriceSplit}
                onDeletePriceSplit={onDeletePriceSplit}
                user_type={user_type}
                deleteOrderItemApiCall={deleteOrderItemApiCall}
                addProductToOrderApiCall={addProductToOrderApiCall}
                employeeOptions={employeeOptions}
                printOrder={printOrder}
                orderSummary={orderSummary}
                shipments={shipments}
                createShipmentFromOrder={createShipmentFromOrder}
                canCreateShipment={allowCreateShipment}
                shipmentCreationReason={order.shipment_creation_reason}
                orderLoading={saving}
              />
            </>
          ) : loading ? (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="card-custom p-4">
                    <FormSectionLoadingSkeleton rows={2} />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_22rem]">
                <div className="space-y-5 min-w-0">
                  <div className="card-custom p-4 sm:p-5">
                    <OperationalDataTableSkeleton
                      columns={[
                        "Product",
                        "Description",
                        "Quantity",
                        "Unit Cost",
                        "Total Cost",
                        "Actions",
                      ]}
                      rows={5}
                    />
                  </div>
                  <div className="card-custom p-4 sm:p-5">
                    <OperationalDataTableSkeleton
                      columns={[
                        "Shipment #",
                        "Status",
                        "Created",
                        "Shipped",
                        "Cases",
                        "Returned",
                        "Credit",
                      ]}
                      rows={3}
                    />
                  </div>
                </div>
                <div className="space-y-5">
                  <div className="card-custom p-4">
                    <FormSectionLoadingSkeleton rows={5} />
                  </div>
                  <div className="card-custom p-4">
                    <FormSectionLoadingSkeleton rows={3} />
                  </div>
                  <div className="card-custom p-4">
                    <FormSectionLoadingSkeleton rows={2} />
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        customClass="max-w-3xl"
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return { user_type: state.auth.user_type };
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(OrderDetails),
);
